<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import { Helper } from '@/mixin/helper'
import NotificationToggle from './NotificationToggle.vue'
import NotificationCheckbox from './NotificationCheckbox.vue'
import TechnicianTable from './TechnicianTable.vue'
import SectionIcon from './SectionIcon.vue'
import * as usersApi from '@/services/rest-api-sms/lib/users/users'
import * as notificationApi from '@/services/rest-api-sms/lib/notification/notification'
import McButton from '@/components/McButton'
import { mapGetters } from 'vuex'

const soundNotification = require('@/assets/sounds/notification_sound_websms.mp3')

export default {
    page: {
        title: 'Notification Settings',
        meta: [{ name: 'description', content: appConfig.description }],
    },
    components: {
        PageHeader,
        McButton,
        NotificationToggle,
        NotificationCheckbox,
        TechnicianTable,
        SectionIcon,
    },

    mixins: [Helper],

    data() {
        return {
            popupNotification: false,
            soundNotification: false,
            woStatusNotification: false,
            technicians: [],
            searchTerm: '',
            notificationTypes: [], // dari API (filtered coordinator)
            selectedNotificationTypes: {}, // key: code, value: boolean
            reportWO: false,
            moveVehicleCompany: false,
            reportNotificationType: null,
            moveVehicleCompanyNotificationType: null,
            hasRule: false,
            initialRule: null,
            initialState: null,
        }
    },

    computed: {
        title() {
            return 'Pengaturan Notifikasi'
        },

        selectedCount() {
            return this.technicians.filter((t) => t.selected).length
        },

        totalCount() {
            return this.technicians.length
        },

        filteredTechnicians() {
            const keyword = this.searchTerm.toLowerCase()

            return [...this.technicians]
                .filter((tech) => tech.fullname.toLowerCase().includes(keyword))
                .sort((a, b) => {
                    // 🔥 selected true ke atas
                    if (a.selected === b.selected) return 0
                    return a.selected ? -1 : 1
                })
        },

        ...mapGetters({
            hasAccessAll: 'identity/hasAccessAll',
        }),
    },

    async mounted() {
        await this.initPage()
        await this.fetchTechnicians()
        await this.saveInitialState()
    },

    methods: {
        saveInitialState() {
            this.initialState = {
                popupNotification: this.popupNotification,
                soundNotification: this.soundNotification,
                woStatusNotification: this.woStatusNotification,

                // 🔥 WAJIB
                selectedNotificationTypes: JSON.parse(JSON.stringify(this.selectedNotificationTypes)),

                reportWO: this.reportWO,
                moveVehicleCompany: this.moveVehicleCompany,

                technicians: this.technicians.map((t) => ({
                    user_id: t.user_id,
                    selected: t.selected,
                })),
            }
        },

        async initPage() {
            const responseRule = await notificationApi.getNotificationRule()
            const rule = responseRule?.data || {}

            this.hasRule = Boolean(rule.notification_rule_id)
            this.initialRule = rule

            // 🔥 INIT TOGGLE BOOLEAN DARI RULE
            this.popupNotification = !!rule.is_pop_up_enabled
            this.soundNotification = !!rule.is_sound_enabled
            this.woStatusNotification = !!rule.wo_status_change_enabled

            const notificationTypesFromRule = Array.isArray(rule.notification_types)
                ? rule.notification_types
                : []

            const enabledCodes = notificationTypesFromRule.map((t) => t.code)
            let params = {
                limit: 1,
                page: 1,
            }
            let responseType = await notificationApi.getNotificationType(params)
            if (responseType.status && responseType.metadata.total_count > 1) {
                params.limit = responseType.metadata.total_count
                responseType = await notificationApi.getNotificationType(params)
            }

            const allCoordinatorTypes =
                responseType.data?.paginated_result.filter((t) => t.related_role === 'coordinator') || []

            // 🔹 REPORT WO
            this.reportNotificationType = allCoordinatorTypes.find((t) => t.code.includes('report'))

            this.reportWO = enabledCodes.includes(this.reportNotificationType?.code)

            this.moveVehicleCompanyNotificationType = allCoordinatorTypes.find((t) => t.code.includes('move_vehicle_company'))

            this.moveVehicleCompany = enabledCodes.includes(this.moveVehicleCompanyNotificationType?.code)

            // 🔹 NON REPORT
            this.notificationTypes = allCoordinatorTypes.filter((t) => !t.code.includes('report') && !t.code.includes('move_vehicle_company'))

            this.selectedNotificationTypes = this.notificationTypes.reduce((acc, type) => {
                acc[type.code] = enabledCodes.includes(type.code)
                return acc
            }, {})
        },

        async fetchTechnicians() {
            this.isLoadingTechnicians = true
            this.technicianError = null

            try {
                let params = {
                    page: 1,
                    limit: 1,
                    filters: [
                        `position_id eq ${process.env.VUE_APP_TECHNICIAN_POSITION_ID}`,
                        `company_id eq ${process.env.VUE_APP_TECHNICIAN_COMPANY_ID}`,
                    ],
                }
                if (this.searchTerm) {
                    params.search = this.searchTerm
                }
                let result = await usersApi.get(params)
                if (result.status) {
                    if (result.metadata.total_count > 1) {
                        params.limit = result.metadata.total_count
                        result = await usersApi.get(params)
                    }

                    this.technicians = result.data.paginated_result.map((tech) => ({
                        ...tech,
                        selected: this.initialRule?.technicians?.includes(tech.user_id) || false,
                    }))
                }
            } catch (error) {
                console.error(error)
                this.technicianError = 'Gagal memuat data teknisi'
                this.$mcToast.error(this.technicianError)
            } finally {
                this.isLoadingTechnicians = false
            }
        },

        handleTechnicianToggle(id) {
            const technician = this.technicians.find((t) => t.user_id === id)
            if (technician) {
                technician.selected = !technician.selected
            }
        },

        handleSelectAll() {
            this.technicians.forEach((tech) => {
                tech.selected = true
            })
        },

        handleDeselectAll() {
            this.technicians.forEach((tech) => {
                tech.selected = false
            })
        },

        buildPatchPayload() {
            return {
                is_pop_up_enabled: this.popupNotification,
                is_sound_enabled: this.soundNotification,
                wo_status_change_enabled: this.woStatusNotification,

                // 🔥 SELALU KIRIM
                notification_type_ids: this.mapNotificationTypeIDs(),
                technician_ids: this.mapTechnicianIDs(),
            }
        },

        isSameArray(a = [], b = []) {
            if (a.length !== b.length) return false
            return [...a].sort().every((val, i) => val === [...b].sort()[i])
        },

        hasChanges() {
            return !this.isSameState(this.initialState, {
                popupNotification: this.popupNotification,
                soundNotification: this.soundNotification,
                woStatusNotification: this.woStatusNotification,
                notificationTypeIds: this.mapNotificationTypeIDs(),
                technicianIds: this.mapTechnicianIDs(),
            })
        },

        isSameState(a, b) {
            return (
                a.popupNotification === b.popupNotification &&
                a.soundNotification === b.soundNotification &&
                a.woStatusNotification === b.woStatusNotification &&
                this.isSameArray(a.notificationTypeIds, b.notificationTypeIds) &&
                this.isSameArray(a.technicianIds, b.technicianIds)
            )
        },

        buildCreatePayload() {
            return {
                is_enabled: true,
                is_pop_up_enabled: this.popupNotification,
                is_sound_enabled: this.soundNotification,
                wo_status_change_enabled: this.woStatusNotification,
                notification_type_ids: this.mapNotificationTypeIDs(),
                technician_ids: this.mapTechnicianIDs(),
            }
        },

        mapNotificationTypeIDs() {
            const ids = this.notificationTypes
                .filter((t) => this.selectedNotificationTypes[t.code])
                .map((t) => t.notification_type_id)

            // 🔥 TAMBAHKAN REPORT WO
            if (this.reportWO && this.reportNotificationType) {
                ids.push(this.reportNotificationType.notification_type_id)
            }

            if (this.moveVehicleCompany && this.moveVehicleCompanyNotificationType) {
                ids.push(this.moveVehicleCompanyNotificationType.notification_type_id)
            }

            return ids
        },

        mapTechnicianIDs() {
            return this.technicians.filter((t) => t.selected).map((t) => t.user_id)
        },

        async saveSettings() {
            try {
                const payload = this.hasRule ? this.buildPatchPayload() : this.buildCreatePayload()

                if (this.hasRule) {
                    await notificationApi.patchNotificationRule(payload)
                } else {
                    await notificationApi.createNotificationRule(payload)
                }

                this.hasRule = true // 🔥 SETELAH POST BERHASIL
                this.saveInitialState()

                // 🔥 SYNC KE GLOBAL STATE
                await this.$store.dispatch('notification/fetchNotificationRule')

                this.$mcToast.success('Pengaturan notifikasi berhasil disimpan')
            } catch (err) {
                this.$mcToast.error('Gagal menyimpan pengaturan')
                console.error(err)
            }
        },

        restoreInitialState() {
            if (!this.initialState) return

            // Toggle & boolean
            this.popupNotification = this.initialState.popupNotification
            this.soundNotification = this.initialState.soundNotification
            this.woStatusNotification = this.initialState.woStatusNotification
            this.reportWO = this.initialState.reportWO

            // Checkbox notification types
            this.selectedNotificationTypes = JSON.parse(
                JSON.stringify(this.initialState.selectedNotificationTypes)
            )

            // Technician selection
            this.technicians = this.technicians.map((t) => {
                const initialTech = this.initialState.technicians.find((i) => i.user_id === t.user_id)
                return {
                    ...t,
                    selected: initialTech?.selected || false,
                }
            })
        },

        cancel() {
            if (this.hasChanges()) {
                this.$bvModal
                    .msgBoxConfirm('Perubahan belum disimpan. Yakin ingin membatalkan?', {
                        okVariant: 'danger',
                    })
                    .then((confirmed) => {
                        if (confirmed) this.restoreInitialState()
                    })
            } else {
                this.restoreInitialState()
            }
        },

        toggleNotification(code, value) {
            this.$set(this.selectedNotificationTypes, code, value)
        },

        onPlay() {
            const audio = new Audio(soundNotification)
            audio.play()
        },
    },
}
</script>

<template>
    <div class="mc-notification-setting pb-5">
        <!-- Header -->
        <div class="mc-notification-setting__header mb-4">
            <PageHeader :title="title" />
            <p class="mc-notification-setting__subtitle">
                Konfigurasikan notifikasi mana yang ingin Anda terima
            </p>
        </div>

        <!-- Pengaturan Umum -->
        <div class="mc-notification-setting__section">
            <div class="mc-notification-setting__section-header">
                <SectionIcon type="settings" />
                <span>Pengaturan Umum</span>
            </div>
            <div class="mc-notification-setting__card">
                <div class="mc-notification-setting__row">
                    <div class="mc-notification-setting__row-content">
                        <div class="mc-notification-setting__row-title">Pop up Notifikasi</div>
                        <div class="mc-notification-setting__row-description">
                            Menampilkan pop up saat ada notifikasi baru
                        </div>
                    </div>
                    <div class="mc-notification-setting__row-actions">
                        <NotificationToggle v-model="popupNotification" />
                    </div>
                </div>
                <div class="mc-notification-setting__row">
                    <div class="mc-notification-setting__row-content">
                        <div class="mc-notification-setting__row-title">Suara notifikasi</div>
                        <div class="mc-notification-setting__row-description">
                            Putar suara saat ada notifikasi baru
                        </div>
                    </div>
                    <div class="mc-notification-setting__row-actions">
                        <b-button variant="outline-secondary" size="sm" class="mc-notification-setting__test-btn"
                            @click="onPlay()">
                            <i class="mc-fa icon-outline_volume-up me-1"></i>
                            Test
                        </b-button>
                        <NotificationToggle v-model="soundNotification" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Notifikasi Work Order -->
        <div class="mc-notification-setting__section">
            <div class="mc-notification-setting__section-header-right">
                <div class="mc-notification-setting__section-header">
                    <SectionIcon type="work-order" />
                    <span>Notifikasi Work Order</span>
                </div>
                <span class="mc-notification-setting__badge">{{ selectedCount }} selected</span>
            </div>
            <div class="mc-notification-setting__card mb-4">
                <div class="mc-notification-setting__row">
                    <div class="mc-notification-setting__row-content">
                        <div class="mc-notification-setting__row-title">
                            Beritahukan tentang perubahan status
                        </div>
                        <div class="mc-notification-setting__row-description">
                            Anda akan menerima pemberitahuan saat status perintah kerja diperbarui oleh teknisi.
                        </div>
                    </div>
                    <div class="mc-notification-setting__row-actions">
                        <NotificationToggle v-model="woStatusNotification" />
                    </div>
                </div>
            </div>

            <!-- Teknisi Section -->
            <div class="mc-notification-setting__section-header-right">
                <div class="mc-notification-setting__section-header">
                    <i class="bx bx-user"></i>
                    <span>Teknisi</span>
                    <span class="mc-notification-setting__badge ms-2">({{ totalCount }} total)</span>
                </div>
                <div class="mc-notification-setting__search">
                    <b-form-input v-model="searchTerm" type="text" placeholder="Search technician..."
                        class="mc-notification-setting__search-input" />
                    <div class="mc-notification-setting__search-actions">
                        <span @click="handleSelectAll" class="mc-notification-setting__link">
                            Select All
                        </span>
                        <span @click="handleDeselectAll" class="mc-notification-setting__link">
                            Deselect all
                        </span>
                    </div>
                </div>
            </div>

            <TechnicianTable :technicians="filteredTechnicians" @toggle="handleTechnicianToggle" />
        </div>

        <!-- Perubahan Status -->
        <div class="mc-notification-setting__section">
            <div class="mc-notification-setting__section-header">
                <SectionIcon type="status" />
                <span>Perubahan Status</span>
            </div>
            <div class="mc-notification-setting__card">
                <div class="px-4 py-4">
                    <div class="mc-notification-setting__row-title">
                        Jenis status yang akan diberitahukan
                    </div>
                    <div class="mc-notification-setting__row-description mb-4">
                        Pilih status work order mana yang akan memicu notifikasi.
                    </div>
                    <div class="mc-notification-setting__status-grid">
                        <NotificationCheckbox v-for="type in notificationTypes" :key="type.notification_type_id"
                            :checked="selectedNotificationTypes[type.code]"
                            @change="toggleNotification(type.code, $event)" :label="type.name" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Notifikasi tambahan -->
        <div class="mc-notification-setting__section">
            <div class="mc-notification-setting__section-header">
                <SectionIcon type="plus" />
                <span>Notifikasi tambahan</span>
            </div>
            <div class="mc-notification-setting__card">
                <div class="mc-notification-setting__row">
                    <div class="mc-notification-setting__row-content">
                        <div class="mc-notification-setting__row-title">Report WO</div>
                        <div class="mc-notification-setting__row-description">
                            Notifikasi Report WO yang belum di selesaikan di bulan ini
                        </div>
                    </div>
                    <div class="mc-notification-setting__row-actions">
                        <NotificationToggle v-model="reportWO" />
                    </div>
                </div>
            </div>
            <div v-if="hasAccessAll(['sms-move-vehicle-company-log:read'])" class="mt-2 mc-notification-setting__card">
                <div class="mc-notification-setting__row">
                    <div class="mc-notification-setting__row-content">
                        <div class="mc-notification-setting__row-title">Pindah Domain</div>
                        <div class="mc-notification-setting__row-description">
                            Notifikasi saat sync data pindah domain selesai dilakukan
                        </div>
                    </div>
                    <div class="mc-notification-setting__row-actions">
                        <NotificationToggle :disabled="!hasAccessAll(['sms-move-vehicle-company-log:update'])"
                            v-model="moveVehicleCompany" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <div class="mc-notification-setting__footer">
            <div class="mc-notification-setting__footer-content">
                <div class="mc-notification-setting__footer-info">
                    {{ selectedCount === totalCount ? 'All selected' : `${selectedCount} selected` }}
                </div>
                <div class="mc-notification-setting__footer-actions">
                    <McButton text="Cancel" variant="secondary" class="mc-notification-setting__btn" @click="cancel" />

                    <McButton text="Save Settings" variant="primary" class="mc-notification-setting__btn"
                        @click="saveSettings" />
                </div>
            </div>
        </div>
    </div>
</template>